import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Button, LinearProgress, Tooltip } from '@material-ui/core';
import { Check, History, VerifiedUser } from '@material-ui/icons';
import clsx from 'clsx';
import { PageEmpty, PAGE_KIT, usePageStyles } from 'components/Page/PageKit';
import { MOBILE_QUERY, useIsMobile } from 'components/Page/useIsMobile';
import { Waypoint } from 'react-waypoint';
import { useQuery } from '@apollo/client';
import _ from 'lodash';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { PveFeedData, PveFeedQuery, PveFeedVars } from '@pvplevelup/core/dist/queries/pve/pve';
import { Dungeons, Raids, getPveInstance, isPveInstanceReady } from '@pvplevelup/core/dist/interfaces/Pve/IPveInstance';
import { IPve } from '@pvplevelup/core/dist/interfaces/Pve/IPve';
import { ClassCounts } from './ClassCounts';
import { PveIcons } from 'utilities/pve_icons';
import { EncounterPicker, EncounterPickerButton } from '../EncounterPicker';
import { formatPveDuration, formatPveNumber, getPveInstanceName, getPveRunState } from './pveFormat';
import { VerifiedBadge } from './VerifiedBadge';

const HARD = '#ff7043';

// note: Electron 8 (Chromium 80) has no flexbox gap; spacing here is grid gap or margins
const useStyles = makeStyles((theme) => ({
  filterPanel: {
    marginBottom: theme.spacing(2),
  },
  filters: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    margin: theme.spacing(0, 0, -1.25, 0),
    '& > *': {
      marginRight: theme.spacing(1.5),
      marginBottom: theme.spacing(1.25),
    },
    [MOBILE_QUERY]: {
      '& > *': {
        marginRight: 0,
      },
    },
  },
  // segmented control: a row of options in a dark well
  segmented: {
    display: 'inline-grid',
    gridAutoFlow: 'column',
    gridGap: 3,
    height: PAGE_KIT.controlHeight,
    boxSizing: 'border-box',
    padding: 3,
    borderRadius: 10,
    background: 'rgba(0, 0, 0, 0.3)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  segment: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridGap: 6,
    alignItems: 'center',
    padding: '0 14px',
    borderRadius: 7,
    fontSize: '0.86rem',
    fontWeight: 700,
    color: PAGE_KIT.muted,
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    transition: 'background 0.12s, color 0.12s',
    '&:hover': {
      color: '#fff',
    },
    '& svg': {
      fontSize: 16,
    },
  },
  segmentActive: {
    color: '#fff',
    background: 'rgba(66, 165, 245, 0.18)',
    boxShadow: `inset 0 -2px 0 ${PAGE_KIT.accent}`,
  },
  segmentHard: {
    color: '#fff',
    background: 'rgba(255, 112, 67, 0.2)',
    boxShadow: `inset 0 -2px 0 ${HARD}`,
  },

  // the list
  scroller: {
    overflowX: 'auto',
  },
  list: {
    minWidth: 860,
    padding: theme.spacing(1.25),
  },
  head: {
    display: 'grid',
    gridGap: theme.spacing(2),
    alignItems: 'center',
    padding: theme.spacing(0.25, 1.5, 1, 1),
    fontSize: '0.68rem',
    fontWeight: 800,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: PAGE_KIT.faint,
    whiteSpace: 'nowrap',
  },
  run: {
    display: 'grid',
    gridGap: theme.spacing(2),
    alignItems: 'center',
    padding: theme.spacing(1, 1.5, 1, 1),
    borderRadius: 12,
    cursor: 'pointer',
    background: 'rgba(255, 255, 255, 0.025)',
    border: '1px solid rgba(255, 255, 255, 0.06)',
    transition: 'background 0.15s, border-color 0.15s, transform 0.15s',
    '& + &': {
      marginTop: theme.spacing(1),
    },
    '&:hover': {
      background: 'rgba(66, 165, 245, 0.06)',
      borderColor: 'rgba(66, 165, 245, 0.35)',
      transform: 'translateY(-1px)',
    },
  },
  thumb: {
    position: 'relative',
    height: 60,
    borderRadius: 9,
    overflow: 'hidden',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.08)',
  },
  kindTag: {
    position: 'absolute',
    left: 5,
    bottom: 5,
    padding: '0 6px',
    borderRadius: 6,
    fontSize: '0.6rem',
    fontWeight: 800,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#fff',
    background: 'rgba(0, 0, 0, 0.65)',
  },
  name: {
    fontSize: '0.98rem',
    fontWeight: 800,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  meta: {
    marginTop: 2,
    fontSize: '0.8rem',
    color: PAGE_KIT.muted,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  pills: {
    display: 'grid',
    gridAutoFlow: 'column',
    justifyContent: 'start',
    gridGap: 6,
  },
  pill: {
    display: 'inline-grid',
    gridAutoFlow: 'column',
    gridGap: 5,
    alignItems: 'center',
    height: 22,
    padding: '0 9px',
    borderRadius: 11,
    fontSize: '0.72rem',
    fontWeight: 800,
    letterSpacing: '0.02em',
    whiteSpace: 'nowrap',
    color: 'rgba(255, 255, 255, 0.8)',
    background: 'rgba(255, 255, 255, 0.07)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    '& svg': {
      fontSize: 13,
    },
  },
  dot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
  },
  stat: {
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
  },
  statValue: {
    fontSize: '0.95rem',
    fontWeight: 800,
  },
  statNone: {
    color: PAGE_KIT.faint,
  },
  bossBar: {
    height: 3,
    marginTop: 5,
    borderRadius: 2,
    overflow: 'hidden',
    background: 'rgba(255, 255, 255, 0.08)',
  },
  group: {
    display: 'grid',
    gridAutoFlow: 'column',
    justifyContent: 'start',
    gridGap: 5,
  },
  // phone layout: one card per run
  mList: {
    padding: theme.spacing(1),
  },
  mTop: {
    display: 'grid',
    gridTemplateColumns: '72px minmax(0, 1fr)',
    gridGap: theme.spacing(1.25),
    alignItems: 'center',
  },
  mBottom: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gridGap: theme.spacing(1),
    alignItems: 'center',
    marginTop: theme.spacing(1),
    fontSize: '0.8rem',
  },
  mGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    padding: '2px 0',
    '& > *': {
      margin: '2px 5px 2px 0',
    },
  },
  date: {
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  dateMain: {
    fontSize: '0.86rem',
    fontWeight: 700,
  },
}));

/** Instances whose bosses have been entered, i.e. the ones that can actually have runs. */
export type PveKind = 'dungeon' | 'raid';

/** Whether an instance is a dungeon or a raid. */
export const getPveKind = (instanceId: number): PveKind => Raids.some((r) => r.id == instanceId) ? 'raid' : 'dungeon';

/** The dungeons and raids that can be run (their bosses are entered); only one kind if given. */
export const getReadyPveInstances = (kind?: PveKind) => (kind == 'dungeon' ? Dungeons : kind == 'raid' ? Raids : Dungeons.concat(Raids))
  .filter(isPveInstanceReady).sort((a, b) => a.name.localeCompare(b.name));

interface Props {
  /** only this character's runs, and show their numbers */
  characterId?: string;
  /** only this user's runs (any of their characters) */
  userId?: string;
}

export interface SegmentOption<T> {
  value: T;
  label: string;
  icon?: React.ReactNode;
  hard?: boolean;
}

/** A segmented control: one of a few options, 40px tall to line up with the other filters (also used by the leaderboard). */
export function Segmented<T>({value, options, onChange}: {value: T, options: SegmentOption<T>[], onChange: (value: T) => void}) {
  const classes = useStyles();
  return (
    <div className={classes.segmented}>
      {options.map((o) => (
        <div key={String(o.value)} role="button" onClick={() => onChange(o.value)}
          className={clsx(classes.segment, {[classes.segmentActive]: value == o.value && !o.hard, [classes.segmentHard]: value == o.value && o.hard})}>
          {o.icon}
          <span>{o.label}</span>
        </div>
      ))}
    </div>
  );
}

export const PveRunList: React.FC<Props> = ({characterId, userId}) => {
  const classes = useStyles();
  const page = usePageStyles();
  const history = useHistory();
  const mobile = useIsMobile();
  const [instance, setInstance] = useState<number | ''>('');
  // all runs by default; "Verified only" narrows to verified runs (see VerifiedBadge)
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [clearedOnly, setClearedOnly] = useState(false);
  const [picking, setPicking] = useState(false);

  const variables: PveFeedVars = {
    cursor: null,
    search: "",
    instance: instance === '' ? null : instance,
    verified: verifiedOnly ? true : null,
    completed: clearedOnly ? true : null,
    character: characterId || null,
    user: userId || null,
  };
  const { data, loading, fetchMore } = useQuery<PveFeedData, PveFeedVars>(PveFeedQuery, {variables, fetchPolicy: 'cache-and-network'});
  const runs = data?.pveFeed?.pves || [];

  const loadMore = () => {
    if(!data?.pveFeed?.hasNext || loading)
      return;
    fetchMore({
      variables: {...variables, cursor: data.pveFeed.next},
      updateQuery: (prev, {fetchMoreResult}) => {
        if(!fetchMoreResult) return prev;
        return {
          ...prev,
          pveFeed: {
            ...fetchMoreResult.pveFeed,
            pves: _.uniqBy([...prev.pveFeed.pves, ...fetchMoreResult.pveFeed.pves], (pve) => pve._id?.toString()),
          },
        };
      },
    });
  }

  /** the page owner's own numbers in a run */
  const ownPerformance = (pve: IPve) => (pve.players || []).find((p) =>
    characterId ? (p.character as any)?._id == characterId : (p.user as any)?._id == userId);

  const showOwn = !!(characterId || userId);
  // one template for the column labels and every row, so they line up
  const columns = `112px minmax(0, 1.4fr) 190px 64px 76px minmax(0, 1fr)${showOwn ? ' 64px 64px' : ''} 104px`;
  const filtered = instance !== '' || verifiedOnly || clearedOnly;

  return (
    <Box>
      <div className={clsx(page.panel, classes.filterPanel)}>
        <div className={classes.filters}>
          <EncounterPickerButton selectedId={instance} onClick={() => setPicking(true)} />
          <Segmented value={verifiedOnly} onChange={setVerifiedOnly} options={[
            {value: false, label: 'All runs'},
            {value: true, label: 'Verified only', icon: <VerifiedUser />},
          ]} />
          <Segmented value={clearedOnly} onChange={setClearedOnly} options={[
            {value: false, label: 'Any result'},
            {value: true, label: 'Cleared only', icon: <Check />},
          ]} />
        </div>
      </div>
      <EncounterPicker open={picking} selectedId={instance} instances={getReadyPveInstances()} allowAll
        title="Filter runs" subtitle="Show runs of one dungeon or raid, or all of them."
        onClose={() => setPicking(false)} onSelect={(i) => { setInstance(i ? i.id : ''); setPicking(false); }} />
      <div className={page.tablePanel}>
        {loading && <LinearProgress style={{position: 'absolute', top: 0, left: 0, right: 0, height: 2}} />}
        {runs.length > 0 && mobile &&
          <div className={classes.mList}>
            {runs.map((pve) => {
              const state = getPveRunState(pve);
              const own = showOwn ? ownPerformance(pve) : null;
              const when = moment(pve.start_time || pve.createdAt);
              const info = pve.instance ? getPveInstance(pve.instance) : null;
              const image = PveIcons[pve.instance || 0];
              const bosses = info?.encounters.length || 0;
              return (
                <div key={pve._id?.toString()} className={classes.run}
                  style={{display: 'block'}} onClick={() => history.push(`/pve/run/${pve._id}`)}>
                  <div className={classes.mTop}>
                    <div className={classes.thumb} style={{height: 48, ...(image ? {backgroundImage: `url(${image})`} : {})}}>
                      {info && <span className={classes.kindTag} style={{left: 3, bottom: 3}}>{getPveKind(info.id) == 'raid' ? 'Raid' : 'Dungeon'}</span>}
                    </div>
                    <div style={{minWidth: 0}}>
                      <div className={classes.name}>{getPveInstanceName(pve)}</div>
                      <div className={classes.pills} style={{marginTop: 4}}>
                        <span className={classes.pill} style={{color: state.color, background: `${state.color}1f`, borderColor: `${state.color}55`}}>
                          <span className={classes.dot} style={{background: state.color}} />{state.label}
                        </span>
                        <VerifiedBadge pve={pve} />
                      </div>
                    </div>
                  </div>
                  <div className={classes.mBottom}>
                    <span className={classes.mGroup}>
                      <ClassCounts players={pve.players || []} size={22} />
                    </span>
                    <span className={classes.stat}>
                      {bosses > 0 && <span><span className={page.muted}>Bosses </span><span className={page.strong}>{(pve.killed || []).length}/{bosses}</span></span>}
                      {pve.completed && <span style={{marginLeft: 10}}><span className={page.muted}>Clear </span><span className={page.strong}>{formatPveDuration(pve.clearTime)}</span></span>}
                      {own && <span style={{marginLeft: 10}}><span className={page.muted}>DPS </span><span className={page.strong}>{formatPveNumber(own.dps)}</span></span>}
                      {own && <span style={{marginLeft: 10}}><span className={page.muted}>HPS </span><span className={page.strong}>{formatPveNumber(own.hps)}</span></span>}
                      <div className={page.muted}>{when.format('MMM D')} · {when.fromNow()}</div>
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        }
        {runs.length > 0 && !mobile &&
          <div className={classes.scroller}>
            <div className={classes.list}>
              <div className={classes.head} style={{gridTemplateColumns: columns}}>
                <span>Dungeon / Raid</span>
                <span />
                <span>Result</span>
                <span style={{textAlign: 'right'}}>Bosses</span>
                <span style={{textAlign: 'right'}}>Clear</span>
                <span>Group</span>
                {showOwn && <span style={{textAlign: 'right'}}>DPS</span>}
                {showOwn && <span style={{textAlign: 'right'}}>HPS</span>}
                <span style={{textAlign: 'right'}}>Date</span>
              </div>
              {runs.map((pve) => {
                const state = getPveRunState(pve);
                const own = showOwn ? ownPerformance(pve) : null;
                const when = moment(pve.start_time || pve.createdAt);
                const info = pve.instance ? getPveInstance(pve.instance) : null;
                const image = PveIcons[pve.instance || 0];
                const bosses = info?.encounters.length || 0;
                const killed = (pve.killed || []).length;
                return (
                  <div key={pve._id?.toString()} className={classes.run}
                    style={{gridTemplateColumns: columns}} onClick={() => history.push(`/pve/run/${pve._id}`)}>
                    <div className={classes.thumb} style={image ? {backgroundImage: `url(${image})`} : undefined}>
                      {info && <span className={classes.kindTag}>{getPveKind(info.id) == 'raid' ? 'Raid' : 'Dungeon'}</span>}
                    </div>
                    <div style={{minWidth: 0}}>
                      <div className={classes.name}>{getPveInstanceName(pve)}</div>
                      <div className={classes.meta}>
                        {[pve.name, pve.server, info ? `${info.maxPlayers} players` : null].filter((x) => x).join(' · ')}
                      </div>
                    </div>
                    <div className={classes.pills}>
                      <span className={classes.pill} style={{color: state.color, background: `${state.color}1f`, borderColor: `${state.color}55`}}>
                        <span className={classes.dot} style={{background: state.color}} />{state.label}
                      </span>
                      <VerifiedBadge pve={pve} />
                    </div>
                    <div className={classes.stat}>
                      {bosses ?
                        <React.Fragment>
                          <span className={classes.statValue}>{killed}</span><span className={page.muted}>/{bosses}</span>
                          <div className={classes.bossBar}>
                            <div style={{width: `${Math.min(100, killed / bosses * 100)}%`, height: '100%', background: state.color}} />
                          </div>
                        </React.Fragment>
                      : <span className={classes.statNone}>-</span>}
                    </div>
                    <div className={classes.stat}>
                      {pve.completed ? <span className={classes.statValue}>{formatPveDuration(pve.clearTime)}</span> : <span className={classes.statNone}>-</span>}
                    </div>
                    <div className={classes.group}>
                      <ClassCounts players={pve.players || []} />
                    </div>
                    {showOwn && <div className={classes.stat}><span className={classes.statValue}>{formatPveNumber(own?.dps)}</span></div>}
                    {showOwn && <div className={classes.stat}><span className={classes.statValue}>{formatPveNumber(own?.hps)}</span></div>}
                    <Tooltip title={when.format('MMM Do YYYY, h:mm a')}>
                      <div className={classes.date}>
                        <div className={classes.dateMain}>{when.format('MMM D, YYYY')}</div>
                        <div className={page.muted}>{when.fromNow()}</div>
                      </div>
                    </Tooltip>
                  </div>
                );
              })}
            </div>
          </div>
        }
        {!loading && runs.length == 0 &&
          <PageEmpty icon={<History />} title={filtered ? 'No runs with these filters' : 'No runs yet'}
            text={filtered ? 'Try another dungeon or raid, or show all runs (not only verified ones) and any result.' : 'Finished dungeon and raid runs show up here, with pulls, damage and healing.'} />
        }
        {data?.pveFeed?.hasNext &&
          <React.Fragment>
            {/* scrolling to the end loads the next page; keyed so it fires again if the end is still showing */}
            <Waypoint key={runs.length} onEnter={loadMore} />
            <Button fullWidth className={page.loadMore} disabled={loading} onClick={loadMore}>Load more runs</Button>
          </React.Fragment>
        }
      </div>
    </Box>
  );
}
